/**
 * Utilitaires partagés pour la résolution des prix et le contexte de simulation.
 * Utilisés dans wizard-container.jsx et summary-component.jsx.
 */

const LENGTH_MAP = {
  'Courts':      'courts',
  'Mi-longs':    'milongs',
  'Longs':       'longs',
  'Extra-longs': 'extralongs',
};

const THICKNESS_MAP = {
  'Fins / Normaux': 'fins',
  'Épais':          'epais',
};

// Steps portant les sélections longueur/épaisseur pour chaque parcours femme
const HAIR_STEPS = ['f-b-1', 'f-c-1', 'f-g-1'];

// ─── Contexte de simulation ───────────────────────────────────────────────────

/**
 * Indique si la simulation est sur le parcours homme.
 * @param {Object} selections
 * @returns {boolean}
 */
export function isHommePath(selections) {
  return Object.keys(selections).some(k =>
    k.startsWith('h-b-') || k.startsWith('h-c-') || k.startsWith('h-b-c-')
  );
}

/**
 * Indique si le mode debug pricing est actif.
 * @returns {boolean}
 */
export function isDebugPricingEnabled() {
  return (
    import.meta.env.VITE_DEBUG_PRICING === 'true' ||
    localStorage.getItem('addict_debug_pricing') === '1'
  );
}

/**
 * Retourne la fréquence sélectionnée pour un choix (selectedValue parsé).
 * Par défaut 1 si non défini ou non parsable.
 * @param {Object} choice
 * @returns {number}
 */
export function getFrequency(choice) {
  return parseInt(choice?.selectedValue, 10) || 1;
}

// ─── Cheveux : labels et clés ─────────────────────────────────────────────────

/**
 * Extrait les clés normalisées longueur/épaisseur depuis les sélections.
 * Cherche dans f-b-1, f-c-1 ou f-g-1 selon le parcours emprunté.
 *
 * @param {Object} selections
 * @returns {{ lengthKey: string|null, thicknessKey: string|null }}
 */
export function getHairKeys(selections) {
  let lengthKey = null;
  let thicknessKey = null;

  for (const stepName of HAIR_STEPS) {
    const stepSelection = selections[stepName];
    if (!stepSelection || typeof stepSelection !== 'object') continue;

    Object.values(stepSelection).forEach((groupSelection) => {
      if (!groupSelection?.choice) return;
      const { label, group } = groupSelection.choice;

      if (group === 'Longeur' && LENGTH_MAP[label]) {
        lengthKey = LENGTH_MAP[label];
      }
      if (group === 'epaisseur' && THICKNESS_MAP[label]) {
        thicknessKey = THICKNESS_MAP[label];
      }
    });

    if (lengthKey !== null || thicknessKey !== null) break;
  }

  return { lengthKey, thicknessKey };
}

/**
 * Retourne les labels affichables longueur/épaisseur (pour le debug et l'UI).
 * Pendant de getHairKeys mais orienté affichage.
 *
 * @param {Object} selections
 * @returns {{ lengthLabel: string|null, thicknessLabel: string|null }}
 */
export function getHairLabels(selections) {
  let lengthLabel = null;
  let thicknessLabel = null;

  for (const stepName of HAIR_STEPS) {
    const stepSelection = selections[stepName];
    if (!stepSelection || typeof stepSelection !== 'object') continue;

    Object.values(stepSelection).forEach(gs => {
      if (gs?.choice?.group === 'Longeur') lengthLabel = gs.choice.label;
      if (gs?.choice?.group === 'epaisseur') thicknessLabel = gs.choice.label;
    });

    if (lengthLabel || thicknessLabel) break;
  }

  return { lengthLabel, thicknessLabel };
}

// ─── Résolution de prix ───────────────────────────────────────────────────────

/**
 * Résout un prix depuis une matrice longueur/épaisseur.
 * Retombe sur la valeur plate si la matrice est absente ou non résolvable.
 *
 * @param {Object} matrix - Objet matrice { courts: { fins, epais }, … }
 * @param {Object} selections
 * @param {number} fallback - Valeur plate de secours
 * @returns {number}
 */
function resolveMatrix(matrix, selections, fallback) {
  if (matrix && typeof matrix === 'object') {
    const { lengthKey, thicknessKey } = getHairKeys(selections);
    if (lengthKey && thicknessKey) {
      const resolved = matrix[lengthKey]?.[thicknessKey];
      if (resolved !== undefined) return Number(resolved);
    }
    if (lengthKey) {
      const resolved = matrix[lengthKey]?.fins ?? matrix[lengthKey]?.epais;
      if (resolved !== undefined) return Number(resolved);
    }
  }
  return Number(fallback) || 0;
}

/**
 * Résout le prix normal (non abonné) d'un choix de config.
 * Utilise `normal-price-matrix` si disponible, sinon `normal-price`.
 *
 * @param {Object} choice - Objet choix issu de la config JSON
 * @param {Object} selections
 * @returns {number}
 */
export function resolveNormalPrice(choice, selections) {
  return resolveMatrix(choice['normal-price-matrix'], selections, choice['normal-price'] ?? 0);
}

/**
 * Résout le prix abonné d'un choix de config.
 * Utilise `subscriber-price-matrix` si disponible, sinon `subscriber-price`.
 *
 * @param {Object} choice - Objet choix issu de la config JSON
 * @param {Object} selections
 * @returns {number}
 */
export function resolveSubscriberPrice(choice, selections) {
  return resolveMatrix(choice['subscriber-price-matrix'], selections, choice['subscriber-price'] ?? 0);
}

/**
 * Résout le prix normal du Brushing (f-0) via sa matrice longueur/épaisseur.
 * Utilisé par les services qui incluent un brushing (coloration, gloss).
 *
 * @param {Object} selections
 * @param {Array} configSteps - Tableau des steps issu de la config JSON
 * @returns {number}
 */
export function getBrushingNormalPrice(selections, configSteps) {
  const f0Step = configSteps.find(s => s.name === 'f-0');
  const brushingChoice = f0Step?.choices?.find(c => c.label === 'Brushing');
  return brushingChoice ? resolveNormalPrice(brushingChoice, selections) : 0;
}
